import { Device } from '@twilio/voice-sdk';
import { Sentry } from 'lib-shared-sentry';
import { Connection, Device as LegacyDevice } from 'twilio-client';

import { appActions } from 'app/app.actions';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

import * as errorsActions from '../../../components/errors/actions/errors';
import { notificationActions } from '../../../components/notifications/actions/notification';
import { hasFeature } from '../../../components/shared/featureFlag';
import { getRequestID } from '../../../components/shared/util/request';
import * as telephonyActions from '../../../components/telephony/actions/telephony';

import { twilioActions } from './actions/twilioActions';

const { telephonyRenewToken } = telephonyActions.actions;
const { errorMicrophoneErrorShowModal } = errorsActions.actions;

declare global {
  interface Window {
    currentConnection: any;
  }
}

// 31208 - The user denied access to the microphone
const twilioErrorCodeMicrophoneDenied = 31208;
// 31003 - The server could not produce a response within a suitable amount of time
const twilioErrorCodeConnectionTimeout = 31003;
// 31205 - JWT token expired.
const twilioErrorCodeConnectionJwtTokenExpired = 31205;
// 31005 - A connection error occurred during the call
const twilioErrorCodeConnectionError = 31005;
// 31000 - An unknown error occurred
const twilioErrorCodeUnknownError = 31000;
// 20104 - Access Token has expired or expiration date is invalid
const twilioErrorCodeAccessTokenExpired = 20104;
// 31009 - No transport available to send or receive messages.
const twilioErrorCodeNoTransportAvailable = 31009;
// 31206 - Rate exceeded authorized limit.
const twilioErrorCodeRateExceeded = 31206;
// 53000 - Signaling connection error
const twilioErrorCodeSignalingConnectionError = 53000;
// 20101 - Twilio was unable to validate your Access Token
const twilioErrorCodeInvalidAccessToken = 20101;

const handledTwilioErrorCodes = [
  twilioErrorCodeMicrophoneDenied,
  twilioErrorCodeConnectionTimeout,
  twilioErrorCodeConnectionJwtTokenExpired,
];

const ignoredTwilioErrorCodes = [
  twilioErrorCodeConnectionError,
  twilioErrorCodeUnknownError,
  twilioErrorCodeAccessTokenExpired,
  twilioErrorCodeNoTransportAvailable,
  twilioErrorCodeRateExceeded,
  twilioErrorCodeSignalingConnectionError,
  twilioErrorCodeInvalidAccessToken,
];

window.currentConnection = null;

const {
  twilioMakeCall,
  twilioMakeCallSucceeded,
  twilioMakeCallFailed,
  twilioMakeInternalCall,
  twilioMakeInternalCallSucceeded,
  twilioMakeInternalCallFailed,
  twilioMakeCallback,
  twilioMakeCallbackSucceeded,
  twilioMakeCallbackFailed,
  twilioInit,
  twilioSendDigits,
  twilioClose,
  twilioConnectCall,
  twilioConnectCallSucceeded,
  twilioConnectCallFailed,
  twilioDisconnectCall,
  twilioDisconnectCallSucceeded,
  twilioFeedback,
  twilioFeedbackSucceeded,
  twilioFeedbackFailed,

  twilioChangeDeviceStatus,
  twilioDeviceError,
  twilioAddActiveCall,
  twilioEstablishedCall,
  twilioMissedCall,
  twilioDisconnectedCall,
} = twilioActions;

const { notificationAdd, notificationRemoveByType } = notificationActions;

function finalizeTwilioOptions(iceServers: Array<any> | null, state: any) {
  const twilioOptions: Partial<LegacyDevice.Options | Device.Options> = {
    debug: false,
    closeProtection: true,
    codecPreferences: [Connection.Codec.Opus],
  };

  const serversLength = iceServers ? iceServers.length : 0;

  // If iceServers looks like a decent candidate to contain configuration
  if (iceServers !== undefined && serversLength > 0) {
    console.info('Configuring Twilio to run in TCP mode');

    // Add it to the final configuration
    twilioOptions.iceServers = iceServers;
  }

  if (hasFeature(state, 'call-noise-suppression')) {
    twilioOptions.audioConstraints = {
      noiseSuppression: false,
      echoCancellation: false,
    };
  }

  return twilioOptions;
}

let twilioDevice: LegacyDevice | Device;
let twilioTokenInterval: ReturnType<typeof setTimeout>;

export const twilioMiddleware = () => (store: any) => {
  const next = store.dispatch;
  const getToken = bindActionToPromise(next, telephonyRenewToken);

  let die = false;
  let offline = true;
  let callWaiting: any = null;

  let isFetchingToken = false;

  const makeCallResponseDeffers = {};
  const makeInternalCallResponseDeffers = {};
  const makeCallbackResponseDeffers = {};
  const connectCallResponseDeffers = {};
  const disconnectCallResponseDeffers = {};

  const handleOnDisconnect = (connection) => {
    window.currentConnection = null;

    const params = Object.fromEntries(
      connection.customParameters ?? new Map([]),
    );

    const { conference, internal, outbound } = connection.message ?? params;
    const status = connection.status();

    next(
      twilioDisconnectedCall({
        message: connection.message ?? params,
      }),
    );

    if (conference && disconnectCallResponseDeffers[conference]) {
      disconnectCallResponseDeffers[conference].resolve({ status: status });
      next(twilioDisconnectCallSucceeded({ status: status }));
      delete disconnectCallResponseDeffers[conference];
    } else if (internal && disconnectCallResponseDeffers[internal]) {
      disconnectCallResponseDeffers[internal].resolve({ status: status });
      next(twilioDisconnectCallSucceeded({ status: status }));
      delete disconnectCallResponseDeffers[internal];
    } else if (outbound && disconnectCallResponseDeffers[outbound]) {
      disconnectCallResponseDeffers[outbound].resolve({ status: status });
      next(twilioDisconnectCallSucceeded({ status: status }));
      delete disconnectCallResponseDeffers[outbound];
    }
  };

  const handleOnConnect = (connection) => {
    const state = store.getState();
    const showTwilioWarnNotifications = hasFeature(
      state,
      'show-twilio-warn-notifications',
    );

    /**
     * The disconnect event have been moved to the connection (Call) in twilio
     * voice-sdk
     */
    connection.on('disconnect', (call) => {
      handleOnDisconnect(call);
    });

    const { conference, internal, outbound, from, callback, muted } =
      connection.message ?? Object.fromEntries(connection.customParameters);
    const status = connection.status();
    const direction = connection._direction;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const csid = conference | callback | null;

    window.currentConnection = connection;

    // If the twilio call is a reconnect call. Eg. a callback the reconnect is an inbound call (aka. conference).
    if (conference && connectCallResponseDeffers[conference]) {
      // Inbound
      connectCallResponseDeffers[conference].resolve({
        status: status,
        direction: direction,
      });

      next(
        twilioConnectCallSucceeded({
          status: status,
          direction: direction,
          csid: csid,
        }),
      );

      delete connectCallResponseDeffers[conference];
    } else if (internal && makeInternalCallResponseDeffers[internal]) {
      // Internal
      makeInternalCallResponseDeffers[internal].resolve({
        status: status,
        direction: direction,
      });

      next(
        twilioMakeInternalCallSucceeded({
          status: status,
          direction: direction,
          userId: internal,
        }),
      );

      delete makeInternalCallResponseDeffers[internal];
    } else if (callback && makeCallbackResponseDeffers[callback]) {
      // Callback
      makeCallbackResponseDeffers[callback].resolve({
        status: status,
        csid: callback,
      });

      next(twilioMakeCallbackSucceeded({ status: status, csid: callback }));

      delete makeCallbackResponseDeffers[callback];
    } else if (outbound && makeCallResponseDeffers[outbound]) {
      // Outbound
      makeCallResponseDeffers[outbound].resolve({
        status: status,
        direction: direction,
      });

      next(
        twilioMakeCallSucceeded({
          status: status,
          direction: direction,
          userId: outbound,
          from: from,
        }),
      );

      delete makeCallResponseDeffers[outbound];
    }

    connection.on('warning', (warningName) => {
      if (showTwilioWarnNotifications) {
        if (warningName === 'low-mos') {
          const id = getRequestID();
          const notificationOptions = {
            id: id,
            type: 'lowQuality',
            title: 'Poor call quality detected (Mean Opinion Score)',
            text: 'We have detected poor call quality conditions on your local connection. You may experience degraded call quality.',
            icon: 'i-info',
            className: 'info',
            showDesktop: true,
          };
          next(notificationRemoveByType({ type: 'lowQuality' }));
          next(notificationAdd(notificationOptions));
        } else if (warningName === 'high-rtt') {
          const notificationOptions = {
            type: 'highRtt',
            title: 'Poor call quality detected (round-trip-time)',
            text: 'We have detected higher call latency on your local connection. You may experience perceptible delays in audio.',
            icon: 'i-info',
            className: 'info',
            showDesktop: true,
          };
          next(notificationRemoveByType({ type: 'highRtt' }));
          next(notificationAdd(notificationOptions));
        } else if (warningName === 'high-jitter') {
          const notificationOptions = {
            type: 'lowJitter',
            title: 'Poor call quality detected (high jitter)',
            text: 'We have detected high jitter on your local connection. You may experience quality problems such as crackling and choppy audio.',
            icon: 'i-info',
            className: 'info',
            showDesktop: true,
          };
          next(notificationRemoveByType({ type: 'lowJitter' }));
          next(notificationAdd(notificationOptions));
        } else if (warningName === 'high-packet-loss') {
          const notificationOptions = {
            type: 'highPacketLoss',
            title: 'Poor call quality detected (high-packet-loss)',
            text: 'We have detected high packet loss on your local connection. You may experience choppy audio or a dropped call.',
            icon: 'i-info',
            className: 'info',
            showDesktop: true,
          };
          next(notificationRemoveByType({ type: 'highPacketLoss' }));
          next(notificationAdd(notificationOptions));
        }
      }

      // Sentry.captureMessage(`Warning detected ${warningName}`, 'info') // one of 'info', 'warning', or 'error'
    });

    connection.on('warning-cleared', (warningName) => {
      console.warn('warning-cleared', warningName);
      if (warningName === 'low-mos') {
        console.warn(warningName);
        // next(notificationRemoveByType({type: 'lowQuality'}))
      } else if (warningName === 'high-rtt') {
        // next(notificationRemoveByType({type: 'highRtt'}))
      } else if (warningName === 'high-jitter') {
        // next(notificationRemoveByType({type: 'lowJitter'}))
      } else if (warningName === 'high-packet-loss') {
        // next(notificationRemoveByType({type: 'highPacketLoss'}))
      }
    });
    next(twilioEstablishedCall({ csid: csid, muted: muted }));
  };

  const getBreadcrumbData = () => ({
    isSetToDie: die,
    isFetchingToken,
    isTwilioLegacy: twilioDevice instanceof LegacyDevice,
    callWaitingType: callWaiting?.type,
  });

  const handleOnUnregistered = (source: string) => () => {
    Sentry.addBreadcrumb({
      category: 'twilio',
      message: `Twilio HandleOnUnregistered: ${source}`,
      level: 'info',
      data: getBreadcrumbData(),
    });
    offline = true;

    next(twilioChangeDeviceStatus(twilioDevice));
    if (die === false && isFetchingToken == false) {
      isFetchingToken = true;
      getToken()
        .then((response) => {
          isFetchingToken = false;
          if (twilioDevice instanceof LegacyDevice) {
            twilioDevice.setup(
              response.data.token,
              finalizeTwilioOptions(
                response.data.ice_servers,
                store.getState(),
              ),
            );
          } else {
            twilioDevice.updateToken(response.data.token);
          }
        })
        .catch((exception) => {
          isFetchingToken = false;
          console.error('Twilio got token exception', exception);
          if (callWaiting !== null) {
            console.error(
              'Twilio create call waiting we should reject it.',
              callWaiting,
            );
            if (
              callWaiting.action &&
              callWaiting.action.meta &&
              typeof callWaiting.action.meta.reject === 'function'
            ) {
              callWaiting.action.meta.reject();
            }

            if (callWaiting.type === 'internal') {
              next(twilioMakeInternalCallFailed());
            } else if (callWaiting.type === 'makeCall') {
              next(twilioMakeCallFailed());
            } else if (callWaiting.type === 'callback') {
              next(twilioMakeCallbackFailed());
            } else {
              next(twilioConnectCallFailed());
            }
            callWaiting = null;
          }
        });
    } else {
      console.info(
        `Twilio offline (die: ${die} is fetching token: ${isFetchingToken})`,
      );
    }
  };

  const handleOnRegistered = (source: string) => () => {
    Sentry.addBreadcrumb({
      category: 'twilio',
      message: `Twilio HandleOnRegistered: ${source}`,
      level: 'info',
      data: getBreadcrumbData(),
    });
    offline = false;
    next(twilioChangeDeviceStatus(twilioDevice));

    //device.audio.incoming(false); // Stop Twilio from playing incoming call sound. (1.4)
    if (callWaiting !== null) {
      // Support makeCall, internalCall, etc.
      if (callWaiting.type === 'internal') {
        onMakeInternalCall(callWaiting.action);
      } else if (callWaiting.type === 'makeCall') {
        onMakeCall(callWaiting.action);
      } else if (callWaiting.type === 'callback') {
        onMakeCallback(callWaiting.action);
      } else {
        onConnectCall(callWaiting.action);
      }
      callWaiting = null;
    }
  };

  const initiateTokenUpdater = () => {
    const ttl = 600000;
    const refreshBuffer = 30000;

    // Update the token every ~10 minute to make sure it's sending
    // telemetry to twilio through long conversations
    if (twilioTokenInterval) clearInterval(twilioTokenInterval);

    twilioTokenInterval = setInterval(async () => {
      const { data } = await getToken();

      twilioDevice.updateToken(data.token);
    }, ttl - refreshBuffer);
  };

  const initTwilio = async (state: any) => {
    const useTwilioVoiceSDK = hasFeature(state, 'use-twilio-voice-sdk');

    if (useTwilioVoiceSDK) {
      console.log('Initializing twilio with @twilio/voice-sdk');

      const { data } = await getToken();
      const options = finalizeTwilioOptions(data.ice_servers, store.getState());

      twilioDevice = new Device(data.token, options);

      twilioDevice.on('registered', handleOnRegistered('onRegistered'));
      twilioDevice.on('unregistered', handleOnUnregistered('onUnregistered'));

      // Twilio docs suggest to trigger this in response to a user
      // interaction be sure sound will be enabled in all browsers
      // https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#deviceregister
      twilioDevice.register();

      // Update the token every ~ 10 minute
      initiateTokenUpdater();
    } else {
      console.log('Initializing twilio with twilio-client');

      twilioDevice = new LegacyDevice();

      twilioDevice.on('ready', handleOnRegistered('onReady'));
      twilioDevice.on('offline', handleOnUnregistered('onOffline'));
    }

    twilioDevice.on('error', (error) => {
      next(twilioDeviceError({ code: error.code, message: error.message }));

      const errorMsg = `Twilio client error (${error.code}): ${error.message}`;
      console.error(errorMsg);

      if (
        !handledTwilioErrorCodes.includes(error.code) &&
        !ignoredTwilioErrorCodes.includes(error.code)
      ) {
        // Capture unknown errors in Sentry
        Sentry.captureMessage(errorMsg, 'error');
      } else {
        // Known errors are captured as breadcrumbs, in case we need to debug other issues
        Sentry.addBreadcrumb({
          category: 'twilio',
          message: errorMsg,
          level: 'error',
        });
      }

      // User denied access to microphone.
      if (error.code === twilioErrorCodeMicrophoneDenied) {
        next(
          notificationAdd({
            title: 'Microphone denied',
            text: error.message,
            className: 'danger',
            icon: 'announcement-slash',
          }),
        );
        next(errorMicrophoneErrorShowModal());
        next(
          appActions.showModal({
            modal: { type: 'app:microphoneError', arg: {} },
          }),
        );
      }

      // Twilio timeout.
      if (error.code === twilioErrorCodeConnectionTimeout) {
        // die = true; // Do not try to reconnect.
        twilioDevice.destroy(); // Destroy Twilio listeners and device.
      }

      // Twilio JWT token expired.
      if (error.code === twilioErrorCodeConnectionJwtTokenExpired) {
        // die = true; // Do not try to reconnect.
        twilioDevice.destroy(); // Destroy Twilio listeners and device.
      }
    });

    twilioDevice.on('incoming', (connection) => {
      next(
        twilioAddActiveCall(
          null,
          connection.parameters.From,
          connection.parameters.To,
          'inbound',
          Date.now(),
        ),
      );
      connection.accept();
    });

    twilioDevice.on('cancel', () => {
      next(twilioMissedCall());
    });

    twilioDevice.on('connect', handleOnConnect);

    // The agent is disconnected from the call.
    twilioDevice.on('disconnect', (connection) => {
      handleOnDisconnect(connection);
    });

    try {
      isFetchingToken = true;
      getToken()
        .then((response) => {
          isFetchingToken = false;
          if (twilioDevice instanceof LegacyDevice) {
            twilioDevice.setup(
              response.data.token,
              finalizeTwilioOptions(
                response.data.ice_servers,
                store.getState(),
              ),
            );
          } else {
            twilioDevice.updateToken(response.data.token);
          }
        })
        .catch((exception) => {
          isFetchingToken = false;
          console.error('Twilio get token exception', exception);
          if (callWaiting !== null) {
            console.error(
              'Twilio create call waiting we should reject it.',
              callWaiting,
            );
            if (
              callWaiting.action &&
              callWaiting.action.meta &&
              typeof callWaiting.action.meta.reject === 'function'
            ) {
              callWaiting.action.meta.reject();
            }

            if (callWaiting.type === 'internal') {
              next(twilioMakeInternalCallFailed());
            } else if (callWaiting.type === 'makeCall') {
              next(twilioMakeCallFailed());
            } else if (callWaiting.type === 'callback') {
              next(twilioMakeCallbackFailed());
            } else {
              next(twilioConnectCallFailed());
            }
            callWaiting = null;
          }
        });
    } catch (e) {
      console.warn('Exception setToken', e);
    }
  };

  // Agent hang up the call conversation.
  const onDisconnectCall = (action): void => {
    const { meta, payload } = action;
    const { csid } = payload;
    console.warn('action', action);
    new Promise(() => {
      if (offline === false && window.currentConnection !== null) {
        if (csid !== undefined) {
          disconnectCallResponseDeffers[csid] = {
            resolve: meta.resolve,
            reject: meta.reject,
          };
        } else {
          console.error(
            'DisconnectCall was not given any csid in payload params',
          );
          meta.reject(
            'Twilio: DisconnectCall was not given any csid in payload params.',
          );
        }

        window.currentConnection.disconnect();
      } else {
        console.error('DisconnectCall is offline or currentConnection is null');
        meta.reject(
          'Twilio: DisconnectCall is offline or currentConnection is null.',
        );
        return;
      }
    });
  };

  const onMakeCall = async (action): Promise<void> => {
    const { payload, meta } = action;
    const { userId, fromNumber, toNumber } = payload;

    const params = {
      outbound: userId,
      from: fromNumber,
      dixaOutboundToPn: toNumber,
    };
    if (offline === false) {
      makeCallResponseDeffers[userId] = {
        resolve: meta.resolve,
        reject: meta.reject,
      };

      if (twilioDevice instanceof LegacyDevice) {
        twilioDevice.connect(params);
      } else {
        const connection = await twilioDevice.connect({ params });
        handleOnConnect(connection);
      }

      next(
        twilioAddActiveCall(
          userId,
          fromNumber,
          toNumber,
          'outbound',
          Date.now(),
        ),
      );
    } else {
      callWaiting = { type: 'makeCall', action: action };
      initTwilio(store.getState());
    }
  };

  const onMakeCallback = async (action): Promise<void> => {
    const { payload, meta } = action;
    const { csid } = payload;

    const params = {
      callback: csid,
    };
    if (offline === false) {
      makeCallbackResponseDeffers[csid] = {
        resolve: meta.resolve,
        reject: meta.reject,
      };

      if (twilioDevice instanceof LegacyDevice) {
        twilioDevice.connect(params);
      } else {
        const connection = await twilioDevice.connect({ params });
        handleOnConnect(connection);
      }

      next(twilioAddActiveCall(csid, 'callback', Date.now()));
    } else {
      callWaiting = { type: 'callback', action: action };
      initTwilio(store.getState());
    }
  };

  const onMakeInternalCall = async (action): Promise<void> => {
    const { payload, meta } = action;
    const { userId } = payload;

    const params = {
      internal: userId,
    };
    if (offline === false) {
      makeInternalCallResponseDeffers[userId] = {
        resolve: meta.resolve,
        reject: meta.reject,
      };

      if (twilioDevice instanceof LegacyDevice) {
        twilioDevice.connect(params);
      } else {
        const connection = await twilioDevice.connect({ params });
        handleOnConnect(connection);
      }

      next(twilioAddActiveCall(userId, 'internal', Date.now()));
    } else {
      callWaiting = { type: 'internal', action: action };
      initTwilio(store.getState());
    }
  };

  const onSendDigits = (action): void => {
    const { digit } = action.payload;
    if (window.currentConnection && digit) {
      window.currentConnection.sendDigits(digit);
    }
  };

  const onConnectCall = async (action): Promise<void> => {
    const { payload, meta } = action;
    const { csid, muted } = payload;

    if (csid === undefined || csid === null) {
      console.error(
        'ConnectCall was not given any csid in payload params',
        action.payload,
      );
      return;
    }

    const params: { conference: any; muted?: 'true' } = {
      conference: csid,
    };
    if (muted !== undefined && muted === true) {
      params.muted = 'true'; // The backend expects that muted is a boolean string.
    }

    if (offline === false) {
      connectCallResponseDeffers[csid] = {
        resolve: meta.resolve,
        reject: meta.reject,
      };

      if (twilioDevice instanceof LegacyDevice) {
        twilioDevice.connect(params);
      } else {
        const connection = await twilioDevice.connect({ params });
        handleOnConnect(connection);
      }
    } else {
      callWaiting = { type: 'connectCall', action: action };
      initTwilio(store.getState());
    }
  };

  const onFeedbackSend = (action): void => {
    const { payload, meta } = action;
    if (window.currentConnection !== null) {
      if (payload.specificIssue !== null) {
        window.currentConnection
          .postFeedback(payload.selectedStar, payload.specificIssue)
          .then((r: any) => {
            next(twilioFeedbackSucceeded(r));
            meta.resolve();
          })
          .catch((e: any) => {
            next(twilioFeedbackFailed(e));
            meta.reject();
          });
      } else {
        window.currentConnection
          .postFeedback(payload.selectedStar)
          .then((r: any) => {
            next(twilioFeedbackSucceeded(r));
            meta.resolve();
          })
          .catch((e) => {
            next(twilioFeedbackFailed(e));
            meta.reject();
          });
      }
    } else {
      next(twilioFeedbackFailed('No current Twilio call connection.'));
      meta.reject();
    }
  };

  const onClose = (_action: any): void => {
    die = true; // Do not try to reconnect.
    twilioDevice.destroy(); // Destroy Twilio listeners and device.
  };

  return (next) => (action) => {
    try {
      if (action.type && action.type.indexOf('TWILIO') > -1) {
        switch (action.type) {
          case twilioMakeCall.toString(): {
            onMakeCall(action);
            break;
          }
          case twilioMakeInternalCall.toString(): {
            onMakeInternalCall(action);
            break;
          }
          case twilioMakeCallback.toString(): {
            onMakeCallback(action);
            break;
          }
          case twilioSendDigits.toString(): {
            onSendDigits(action);
            break;
          }
          case twilioConnectCall.toString(): {
            onConnectCall(action);
            break;
          }
          case twilioDisconnectCall.toString(): {
            onDisconnectCall(action);
            break;
          }
          case twilioFeedback.toString(): {
            onFeedbackSend(action);
            break;
          }
          case twilioClose.toString(): {
            onClose(action);
            break;
          }
          case twilioInit.toString(): {
            initTwilio(store.getState());
            break;
          }
        }
      }
    } catch (exception) {
      Sentry.captureException(exception);
    }

    return next(action);
  };
};
